import React, { useCallback, useEffect, useRef, useState } from 'react';
import { API_URL, apiFetch } from '../config/api';
import useVoiceLive from '../hooks/useVoiceLive';
import { usePushToTalk, speak } from '../hooks/usePushToTalk';
import { buildCommand } from '../utils/flightCommands';
import { onVoiceLiveRequest, publishVoiceLiveState, requestDroneFocus } from '../utils/voiceLiveBus';

/**
 * Voice + chat agent — talk or type, the backend translates, you confirm, the
 * ordinary flight-control endpoints execute.
 *
 * Two input paths, one safety spine and one transcript:
 *   • Hold to talk — MediaRecorder → /api/nl/stt → /api/nl/command
 *   • Live         — continuous audio to Gemini Live over the backend proxy
 *
 * Neither reaches a vehicle by any route a button does not already use, so
 * authorizeDroneAccess, the connected check and the mission-running LAND block
 * all still run. /api/nl only ever returns an intent.
 */

// Bottom-right footprint of the panel and its collapsed bubble.
const PANEL_WIDTH = 360;
const BUBBLE_SIZE = 54;

const ALERT_COLORS = { CRITICAL: 'var(--danger)', WARNING: 'var(--warning)', INFO: 'var(--accent)' };

function Icon({ name, size = 18, color, fill = false, style = {} }) {
    return (
        <span
            className="material-symbols-outlined"
            style={{ fontSize: size, color, fontVariationSettings: fill ? "'FILL' 1" : "'FILL' 0", ...style }}
        >{name}</span>
    );
}

function VoiceAgent({ selectedDroneId = null, dronesTelemetry = {} }) {
    const [open, setOpen] = useState(false);
    const [draft, setDraft] = useState('');
    const [intent, setIntent] = useState(null);
    const [activeTrigger, setActiveTrigger] = useState(null);
    const [alerts, setAlerts] = useState([]);
    const [status, setStatus] = useState('');
    const [busy, setBusy] = useState(false);
    const [showSettings, setShowSettings] = useState(false);
    // One transcript for both input paths. Also the conversation memory sent back
    // with each typed turn, so "usko land karao" knows who "usko" is.
    const [chat, setChat] = useState([]);   // [{ role: 'user'|'model'|'system', text }]
    // SITL only. A real vehicle keeps the confirm tap.
    const [autoRun, setAutoRun] = useState(false);
    // Off by default: barge-in is worth keeping. Turn on if the agent starts
    // transcribing its own voice as operator speech on laptop speakers.
    const [halfDuplex, setHalfDuplex] = useState(false);

    const chatRef = useRef(chat);
    const autoRunRef = useRef(autoRun);
    const scrollRef = useRef(null);
    const liveRef = useRef(null);
    const activeTriggerRef = useRef(activeTrigger);

    useEffect(() => { chatRef.current = chat; }, [chat]);
    useEffect(() => { autoRunRef.current = autoRun; }, [autoRun]);
    useEffect(() => { activeTriggerRef.current = activeTrigger; }, [activeTrigger]);

    const push = useCallback((turn) => setChat((c) => [...c, turn].slice(-40)), []);

    const cancelTrigger = useCallback((reason = 'Cancelled by operator') => {
        const cur = activeTriggerRef.current;
        if (!cur) return;
        setActiveTrigger(null);
        push({ role: 'system', ok: false, text: `Trigger cancelled: ${cur.description} (${reason})` });
        if (liveRef.current?.state === 'live') {
            liveRef.current.sendText(`[system] Conditional trigger "${cur.description}" on ${cur.droneId} was CANCELLED: ${reason}.`);
        } else {
            speak('Trigger cancel ho gaya.');
        }
    }, [push]);

    // Pin to the newest message. An operator reading the last line should not
    // have to scroll while the agent is still talking.
    useEffect(() => {
        const el = scrollRef.current;
        if (el) el.scrollTop = el.scrollHeight;
    }, [chat, status, intent, activeTrigger]);

    const execute = useCallback(async (candidate) => {
        // Camera move, not a command — nothing to POST.
        if (candidate?.action === 'focus') {
            if (candidate?.droneId) requestDroneFocus(candidate.droneId);
            setIntent(null);
            return;
        }

        // Safety: If an operator sends a manual command to a drone with a pending trigger, cancel the old trigger
        if (activeTriggerRef.current && candidate?.droneId === activeTriggerRef.current.droneId && !candidate?.isTriggerExecution) {
            cancelTrigger('New manual command sent');
        }

        const command = buildCommand(candidate);
        if (!command) return;
        const { endpoint, body } = command;
        setBusy(true);
        setStatus('Sending…');
        let outcome;
        try {
            const res = await apiFetch(`${API_URL}${endpoint}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(body),
            });
            const data = await res.json();
            // "Accepted", never "succeeded". The endpoint returning ok means the
            // command reached the flight controller — not that the aircraft did
            // the thing. The agent was turning "succeeded" into "ud gaya hai".
            outcome = data.success
                ? `${candidate?.action} on ${candidate?.droneId} was ACCEPTED by the flight controller. `
                    + `${data.message || ''} This means the command was received. It does NOT mean the drone `
                    + 'has armed, climbed, moved or arrived — call get_fleet_status before saying anything about that.'
                : `${candidate?.action} on ${candidate?.droneId} was REJECTED: ${data.message || 'no reason given'}`;
            push({ role: 'system', ok: !!data.success, text: `${candidate?.action} · ${data.message || (data.success ? 'accepted' : 'rejected')}` });
            setStatus('');
            if (liveRef.current?.state !== 'live') speak(data.success ? 'Done' : `Rejected. ${data.message || ''}`);
        } catch (error) {
            outcome = `${candidate?.action} on ${candidate?.droneId} failed to send: ${error.message}`;
            push({ role: 'system', ok: false, text: `${candidate?.action} · ${error.message}` });
            setStatus('');
        } finally {
            setBusy(false);
            setIntent(null);
        }
        // Close the loop. Without this the agent never learns what became of the
        // action it proposed, so it keeps asking for a confirmation that already
        // happened.
        if (outcome && liveRef.current?.state === 'live') {
            liveRef.current.sendText(`[system] ${outcome} Report this to the operator in one short line.`);
        }
    }, [cancelTrigger, push]);

    const handleIncomingIntent = useCallback((proposed) => {
        if (!proposed) return;
        if (proposed.isConditional && proposed.condition) {
            const curTel = dronesTelemetry?.[proposed.droneId];
            const trig = {
                id: `trig_${Date.now()}`,
                droneId: proposed.droneId,
                action: proposed.action,
                mode: proposed.mode,
                missionId: proposed.missionId,
                condition: proposed.condition,
                description: proposed.description || `${proposed.action} on condition`,
                initialAlt: Number.isFinite(Number(curTel?.alt ?? curTel?.alt_msl)) ? Number(curTel?.alt ?? curTel?.alt_msl) : 0,
                initialMode: curTel?.mode,
                createdAt: Date.now(),
            };
            setActiveTrigger(trig);
            push({
                role: 'system',
                ok: true,
                text: `⚡ Trigger Armed: ${trig.description} (${trig.droneId})`,
            });
            if (proposed.say && liveRef.current?.state !== 'live') {
                speak(proposed.say);
            }
            setIntent(null);
            return;
        }

        setIntent(proposed);
        if (autoRunRef.current) execute(proposed);
    }, [dronesTelemetry, execute, push]);

    // Watch telemetry to evaluate active conditional trigger
    useEffect(() => {
        const trig = activeTrigger;
        if (!trig) return;
        const tel = dronesTelemetry?.[trig.droneId];
        if (!tel) return;

        const now = Date.now();
        // 1. Timeout after 10 mins
        if (now - trig.createdAt > 10 * 60 * 1000) {
            cancelTrigger('Timeout after 10 minutes');
            return;
        }

        // 2. Disarm check
        if (tel.armed === false && trig.action !== 'disarm') {
            cancelTrigger('Drone was disarmed');
            return;
        }

        // 3. Condition check
        let triggered = false;
        const cond = trig.condition;

        if (cond.type === 'altitude_reached') {
            const curAlt = Number(tel.alt ?? tel.alt_msl);
            const targetAlt = Number(cond.targetAltitude);
            if (Number.isFinite(curAlt) && Number.isFinite(targetAlt)) {
                const isClimbing = trig.initialAlt < targetAlt;
                if (isClimbing ? curAlt >= targetAlt - 1.5 : curAlt <= targetAlt + 1.5) {
                    triggered = true;
                } else if (Math.abs(curAlt - targetAlt) <= 1.5) {
                    triggered = true;
                }
            }
        } else if (cond.type === 'battery_below') {
            const bat = Number(tel.battery_remaining);
            if (Number.isFinite(bat) && Number.isFinite(cond.batteryPct) && bat <= cond.batteryPct) {
                triggered = true;
            }
        } else if (cond.type === 'arrived') {
            const speed = Number(tel.groundspeed);
            if (Number.isFinite(speed) && speed < 0.8) {
                triggered = true;
            }
        }

        if (triggered) {
            setActiveTrigger(null);
            push({
                role: 'system',
                ok: true,
                text: `⚡ Trigger Met: ${trig.description} → Executing ${trig.action}`,
            });

            execute({
                action: trig.action,
                droneId: trig.droneId,
                mode: trig.mode,
                missionId: trig.missionId,
                isTriggerExecution: true,
            });

            if (liveRef.current?.state === 'live') {
                liveRef.current.sendText(
                    `[system] Condition met: ${trig.description} on ${trig.droneId}. Command "${trig.action}" has been executed automatically. Report this to the operator in one short sentence.`
                );
            } else {
                speak(`${trig.description} poora ho gaya. ${trig.action} command bhej di hai.`);
            }
        }
    }, [dronesTelemetry, activeTrigger, execute, cancelTrigger, push]);

    const parse = useCallback(async (text) => {
        push({ role: 'user', text });
        setDraft('');
        setBusy(true);
        setStatus('Thinking…');
        setIntent(null);
        setAlerts([]);
        try {
            const res = await apiFetch(`${API_URL}/nl/command`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    text,
                    selectedDroneId,
                    history: chatRef.current.filter((t) => t.role !== 'system'),
                }),
            });
            const data = await res.json();
            if (!data.success) {
                setStatus('');
                push({ role: 'system', ok: false, text: data.message });
                return;
            }
            const parsed = data.intent;
            setStatus('');
            if (parsed?.say) {
                speak(parsed.say);
                push({ role: 'model', text: parsed.say, detail: parsed.detail || null });
            }

            // Surface whatever the alert engine was actually holding, next to the
            // answer that was built from it — so a spoken "all clear" is checkable.
            const scope = (parsed && parsed.droneId)
                ? (data.fleet || []).filter((d) => d && d.id === parsed.droneId)
                : (data.fleet || []);
            setAlerts(scope.flatMap((d) => (d?.alerts || []).map((a) => ({ ...a, droneId: d?.id }))));

            if (parsed?.action === 'focus' && parsed?.droneId) requestDroneFocus(parsed.droneId);

            if (parsed?.isConditional && parsed?.condition) {
                handleIncomingIntent(parsed);
            } else if (parsed?.needsConfirm) {
                setIntent(parsed);
                if (autoRunRef.current) await execute(parsed);
            }
        } catch (error) {
            setStatus('');
            push({ role: 'system', ok: false, text: error.message });
        } finally {
            setBusy(false);
        }
    }, [selectedDroneId, handleIncomingIntent, execute, push]);

    const live = useVoiceLive({
        selectedDroneId,
        halfDuplex,
        onTurn: push,
        onIntent: handleIncomingIntent,
        onFocus: requestDroneFocus,
        onFleet: (fleetNow) => setAlerts(
            (fleetNow || []).filter(Boolean).flatMap((d) => (d?.alerts || []).map((a) => ({ ...a, droneId: d?.id })))
        ),
    });
    liveRef.current = live;

    // Mirror the switch into the session, including a mid-session flip. Depend on
    // the two stable values, not on `live` — that object is new every render.
    const { state: liveState, setAutoRun: liveSetAutoRun } = live;
    useEffect(() => {
        if (liveState === 'live') liveSetAutoRun(autoRun);
    }, [autoRun, liveState, liveSetAutoRun]);

    const { listening, transcribing, start: startListening, stop: stopListening, mic } = usePushToTalk({
        onText: parse,
        onStatus: setStatus,
        onError: (message) => push({ role: 'system', ok: false, text: message }),
    });

    // The map popup's LIVE button drives this one session — there is only ever
    // one, and it is already pointed at whichever drone is selected.
    useEffect(() => onVoiceLiveRequest(() => {
        const session = liveRef.current;
        if (!session) return;
        if (session.state === 'live' || session.state === 'connecting') {
            session.stop();
        } else {
            setOpen(true);
            session.start();
        }
    }), []);

    useEffect(() => { publishVoiceLiveState(liveState); }, [liveState]);

    if (!open) {
        return (
            <button
                onClick={() => setOpen(true)}
                title="Voice agent"
                style={{
                    // --voice-* are set by MobileView.css on phones (above the tab bar)
                    position: 'fixed', right: 'var(--voice-right, 20px)', bottom: 'var(--voice-bottom, 20px)', zIndex: 'var(--voice-z, 4000)',
                    width: BUBBLE_SIZE, height: BUBBLE_SIZE, borderRadius: '50%', cursor: 'pointer',
                    background: 'var(--voice-fab-bg, color-mix(in srgb, var(--accent) 12%, transparent))', border: '1px solid color-mix(in srgb, var(--accent) 50%, transparent)',
                    color: 'var(--accent)', display: 'flex', alignItems: 'center', justifyContent: 'center',
                    boxShadow: 'var(--voice-fab-shadow, 0 4px 20px rgba(0,0,0,.5))', margin: 0, marginBottom: 0, padding: 0,
                }}
            ><Icon name="graphic_eq" size={24} color="var(--voice-fab-fg, var(--accent))" /></button>
        );
    }

    const liveOn = live.state === 'live';
    const stateLabel = liveOn ? 'LIVE' : live.state === 'connecting' ? 'CONNECTING' : listening ? 'RECORDING' : 'IDLE';
    const stateColor = liveOn ? 'var(--success)' : live.state === 'connecting' ? 'var(--warning)'
        : listening ? 'var(--danger)' : 'var(--stitch-outline)';

    return (
        <div style={{
            position: 'fixed', right: 'var(--voice-right, 20px)', bottom: 'var(--voice-bottom, 20px)', zIndex: 'var(--voice-z, 4000)',
            width: `var(--voice-w, ${PANEL_WIDTH}px)`,
            height: showSettings
                ? 'min(580px, calc(100vh - var(--map-controls-height, 0px) - 44px))'
                : 'min(460px, calc(100vh - var(--map-controls-height, 0px) - 44px))',
            maxHeight: 'var(--voice-max-h, calc(100vh - var(--map-controls-height, 0px) - 40px))',
            display: 'flex', flexDirection: 'column',
            background: 'var(--stitch-surface-lowest, #0d0e0f)', color: 'var(--textPrimary)',
            border: '1px solid color-mix(in srgb, var(--accent) 28%, transparent)', borderRadius: 12,
            boxShadow: '0 12px 40px rgba(0,0,0,.6)', fontSize: 13, overflow: 'hidden',
            boxSizing: 'border-box',
            transition: 'height 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
        }}>
            {/* ── header ─────────────────────────────────────────────── */}
            <div style={{
                display: 'flex', alignItems: 'center', gap: 10, padding: '12px 14px',
                borderBottom: '1px solid color-mix(in srgb, var(--borderSecondary) 40%, transparent)', background: 'color-mix(in srgb, var(--accent) 5%, transparent)',
                flexShrink: 0,
            }}>
                <Icon name="graphic_eq" size={20} color="var(--accent)" />
                <span className="stitch-label" style={{ fontSize: 12, letterSpacing: '.08em', color: 'var(--accent)' }}>
                    VOICE AGENT
                </span>
                <span style={{
                    display: 'inline-flex', alignItems: 'center', gap: 5, marginLeft: 'auto',
                    fontSize: 10, letterSpacing: '.08em', color: stateColor,
                }}>
                    <span style={{ width: 6, height: 6, borderRadius: '50%', background: stateColor }} />
                    {stateLabel}
                </span>
                <button onClick={() => setShowSettings((s) => !s)} title="Settings"
                    style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, display: 'flex', width: 'auto', margin: 0, marginBottom: 0 }}>
                    <Icon name="tune" size={18} color={showSettings ? 'var(--accent)' : 'var(--stitch-outline)'} />
                </button>
                <button onClick={() => { stopListening(); live.stop(); setOpen(false); }} title="Close"
                    style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, display: 'flex', width: 'auto', margin: 0, marginBottom: 0 }}>
                    <Icon name="close" size={18} color="var(--stitch-outline)" />
                </button>
            </div>

            {/* ── settings drawer ────────────────────────────────────── */}
            {showSettings && (
                <div style={{ padding: '10px 14px', borderBottom: '1px solid color-mix(in srgb, var(--borderSecondary) 40%, transparent)', background: 'color-mix(in srgb, var(--bgTertiary) 20%, transparent)', flexShrink: 0, maxHeight: 180, overflowY: 'auto' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 11, marginBottom: 8, cursor: 'pointer' }}>
                        <input type="checkbox" checked={autoRun} onChange={(e) => setAutoRun(e.target.checked)} />
                        <span>Auto-run without confirm — <strong style={{ color: 'var(--warning)' }}>SITL only</strong></span>
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 11, marginBottom: 8, cursor: 'pointer' }}>
                        <input type="checkbox" checked={halfDuplex} onChange={(e) => setHalfDuplex(e.target.checked)} />
                        <span>Mute mic while agent speaks — only if it echoes itself</span>
                    </label>
                    <div style={{ fontFamily: 'monospace', fontSize: 10, color: 'var(--textMuted)' }}>{mic.facts}</div>
                    {chat.length > 0 && (
                        <button onClick={() => { setChat([]); setAlerts([]); setStatus(''); live.clearText(); }}
                            style={{
                                marginTop: 8, marginBottom: 0, padding: '4px 10px', fontSize: 11, cursor: 'pointer', borderRadius: 6,
                                background: 'transparent', color: 'var(--textMuted)', border: '1px solid color-mix(in srgb, var(--borderSecondary) 60%, transparent)',
                                width: 'auto', display: 'inline-block',
                            }}>Clear conversation</button>
                    )}
                </div>
            )}

            {mic.reason && (
                <div style={{ padding: '8px 14px', color: 'var(--warning)', fontSize: 11, lineHeight: 1.5, background: 'color-mix(in srgb, var(--warning) 6%, transparent)', flexShrink: 0 }}>
                    {mic.reason} Tab tak neeche type kar sakte ho.
                </div>
            )}

            {/* ── active conditional trigger badge ───────────────────── */}
            {activeTrigger && (
                <div style={{
                    padding: '8px 14px',
                    background: 'color-mix(in srgb, var(--accent) 8%, transparent)',
                    borderBottom: '1px solid color-mix(in srgb, var(--accent) 28%, transparent)',
                    display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0,
                }}>
                    <span className="material-symbols-outlined" style={{ fontSize: 18, color: 'var(--accent)' }}>
                        timer
                    </span>
                    <div style={{ flex: 1, fontSize: 11, lineHeight: 1.3 }}>
                        <div style={{ color: 'var(--accent)', fontWeight: 600, letterSpacing: '.04em' }}>
                            TRIGGER ACTIVE: {activeTrigger.description}
                        </div>
                        {dronesTelemetry?.[activeTrigger.droneId] && (
                            <div style={{ color: 'var(--textMuted)', fontSize: 10, marginTop: 2 }}>
                                {activeTrigger.condition.type === 'altitude_reached' && (
                                    <>Current Alt: {Math.round(dronesTelemetry[activeTrigger.droneId].alt ?? dronesTelemetry[activeTrigger.droneId].alt_msl ?? 0)} m / Target: {activeTrigger.condition.targetAltitude} m</>
                                )}
                                {activeTrigger.condition.type === 'battery_below' && (
                                    <>Battery: {dronesTelemetry[activeTrigger.droneId].battery_remaining}% / Limit: {activeTrigger.condition.batteryPct}%</>
                                )}
                                {activeTrigger.condition.type === 'arrived' && (
                                    <>Speed: {(dronesTelemetry[activeTrigger.droneId].groundspeed ?? 0).toFixed(1)} m/s</>
                                )}
                            </div>
                        )}
                    </div>
                    <button
                        onClick={() => cancelTrigger('Operator cancelled')}
                        style={{
                            margin: 0, marginBottom: 0, padding: '3px 8px', borderRadius: 4,
                            background: 'transparent', border: '1px solid color-mix(in srgb, var(--danger) 60%, transparent)',
                            color: 'var(--danger)', fontSize: 10, cursor: 'pointer',
                        }}
                    >
                        Cancel
                    </button>
                </div>
            )}

            {/* ── transcript ─────────────────────────────────────────── */}
            <div ref={scrollRef} style={{ flex: '1 1 0px', minHeight: 60, overflowY: 'auto', padding: '12px 14px' }}>
                {chat.length === 0 && !live.userText && !live.modelText && (
                    <div style={{ color: 'var(--textMuted)', fontSize: 12, lineHeight: 1.6 }}>
                        Bolo ya likho:<br />
                        <span style={{ color: 'var(--stitch-outline)' }}>“battery kitni hai”</span><br />
                        <span style={{ color: 'var(--stitch-outline)' }}>“drone ko 30 meter uthao”</span><br />
                        <span style={{ color: 'var(--stitch-outline)' }}>“koi alert hai?”</span>
                    </div>
                )}

                {chat.map((turn, i) => (
                    <Bubble key={i} turn={turn} />
                ))}

                {/* in-flight live turn, still streaming */}
                {live.userText && <Bubble turn={{ role: 'user', text: live.userText }} pending />}
                {live.modelText && <Bubble turn={{ role: 'model', text: live.modelText }} pending />}

                {status && (
                    <div style={{ color: 'var(--textMuted)', fontSize: 11, fontStyle: 'italic', marginTop: 6 }}>{status}</div>
                )}
            </div>

            {/* ── alerts ─────────────────────────────────────────────── */}
            {alerts.length > 0 && (
                <div style={{ padding: '8px 14px', borderTop: '1px solid color-mix(in srgb, var(--borderSecondary) 40%, transparent)', maxHeight: 80, overflowY: 'auto', flexShrink: 0 }}>
                    {alerts.map((a, i) => (
                        <div key={i} style={{ display: 'flex', gap: 6, fontSize: 11, marginBottom: 4, color: ALERT_COLORS[a.level] || 'inherit' }}>
                            <Icon name="warning" size={13} color={ALERT_COLORS[a.level]} />
                            <span style={{ flex: 1, lineHeight: 1.4 }}>{a.message}</span>
                        </div>
                    ))}
                </div>
            )}

            {/* ── confirmation ───────────────────────────────────────── */}
            {intent && !autoRun && (
                <div style={{ padding: '12px 14px', borderTop: '1px solid color-mix(in srgb, var(--warning) 40%, transparent)', background: 'color-mix(in srgb, var(--warning) 8%, transparent)', flexShrink: 0 }}>
                    <div className="stitch-label" style={{ fontSize: 10, letterSpacing: '.08em', color: 'var(--warning)', marginBottom: 6 }}>
                        CONFIRM COMMAND
                    </div>
                    <div style={{ fontFamily: 'monospace', fontSize: 12, marginBottom: 4 }}>
                        {intent?.action}
                        <span style={{ color: 'var(--textMuted)' }}>
                            {intent?.droneId ? ` · ${intent.droneId}` : ''}
                            {intent?.direction ? ` · ${intent.distance} m ${intent.direction}` : ''}
                            {intent?.altitude != null ? ` · ${Math.round(intent.altitude)} m alt` : ''}
                            {intent.mode ? ` · ${intent.mode}` : ''}
                            {intent.missionId ? ` · ${intent.missionId}` : ''}
                            {intent.lat != null ? ` · ${intent.lat.toFixed(5)}, ${intent.lon.toFixed(5)}` : ''}
                        </span>
                    </div>
                    {intent.detail && <div style={{ fontSize: 11, color: 'var(--textMuted)', marginBottom: 8, whiteSpace: 'pre-wrap' }}>{intent.detail}</div>}
                    <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                        <button onClick={() => execute(intent)} disabled={busy}
                            style={{
                                flex: 1, margin: 0, marginBottom: 0, padding: '8px', borderRadius: 6, border: 'none', cursor: 'pointer',
                                background: 'var(--success)', color: '#04140d', fontWeight: 600, letterSpacing: '.04em',
                            }}>CONFIRM</button>
                        <button onClick={() => { setIntent(null); setStatus('Cancelled'); }}
                            style={{
                                width: 'auto', margin: 0, marginBottom: 0, padding: '8px 16px', borderRadius: 6, cursor: 'pointer', background: 'transparent',
                                color: 'var(--textMuted)', border: '1px solid color-mix(in srgb, var(--borderSecondary) 70%, transparent)',
                            }}>Cancel</button>
                    </div>
                </div>
            )}

            {/* ── composer ───────────────────────────────────────────── */}
            <div style={{ padding: '10px 14px 12px', borderTop: '1px solid color-mix(in srgb, var(--borderSecondary) 40%, transparent)', flexShrink: 0, background: 'var(--stitch-surface-lowest, #0d0e0f)' }}>
                {live.error && (
                    <div style={{ color: 'var(--danger)', fontSize: 11, marginBottom: 8, lineHeight: 1.4 }}>{live.error}</div>
                )}
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    {/* Compact Mic Toggle Button */}
                    <button
                        type="button"
                        onClick={() => {
                            if (listening) {
                                stopListening();
                            } else {
                                startListening();
                            }
                        }}
                        disabled={!mic.ok || busy || transcribing || liveOn}
                        title={listening ? 'Recording… Click to stop & send' : mic.ok ? 'Click to speak' : mic.reason}
                        style={{
                            width: 36,
                            minWidth: 36,
                            maxWidth: 36,
                            height: 38,
                            margin: 0,
                            marginBottom: 0,
                            padding: 0,
                            borderRadius: 8,
                            cursor: mic.ok && !busy && !transcribing && !liveOn ? 'pointer' : 'not-allowed',
                            background: listening ? 'var(--danger, var(--danger))' : 'color-mix(in srgb, var(--bgTertiary) 45%, transparent)',
                            border: `1px solid ${listening ? 'var(--danger, var(--danger))' : 'color-mix(in srgb, var(--borderSecondary) 60%, transparent)'}`,
                            color: listening ? '#ffffff' : 'var(--accent)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0,
                            boxSizing: 'border-box',
                            transition: 'all .2s ease',
                            opacity: !mic.ok || liveOn ? 0.4 : 1,
                        }}
                    >
                        <Icon name={listening ? 'stop' : 'mic'} size={17} fill={listening} color={listening ? '#ffffff' : 'var(--accent)'} />
                    </button>

                    {/* Text Input Box */}
                    <input
                        value={draft}
                        onChange={(e) => setDraft(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter' && draft.trim() && !busy) parse(draft.trim()); }}
                        placeholder={listening ? 'Listening to speech…' : liveOn ? 'Live conversation active…' : 'Type a command…'}
                        disabled={busy}
                        style={{
                            flex: 1,
                            minWidth: 0,
                            height: 38,
                            padding: '0 10px',
                            borderRadius: 8,
                            boxSizing: 'border-box',
                            fontSize: 13,
                            border: `1px solid ${listening ? 'var(--danger, var(--danger))' : liveOn ? 'var(--success, var(--success))' : 'color-mix(in srgb, var(--borderSecondary) 60%, transparent)'}`,
                            background: 'rgba(0, 0, 0, 0.4)',
                            color: 'inherit',
                            outline: 'none',
                        }}
                    />

                    {/* Submit / Send Button */}
                    <button
                        type="button"
                        onClick={() => draft.trim() && !busy && parse(draft.trim())}
                        disabled={!draft.trim() || busy}
                        title="Send command"
                        style={{
                            width: 36,
                            minWidth: 36,
                            maxWidth: 36,
                            height: 38,
                            margin: 0,
                            marginBottom: 0,
                            padding: 0,
                            borderRadius: 8,
                            cursor: draft.trim() && !busy ? 'pointer' : 'not-allowed',
                            background: draft.trim() && !busy ? 'color-mix(in srgb, var(--accent) 18%, transparent)' : 'color-mix(in srgb, var(--bgTertiary) 35%, transparent)',
                            border: `1px solid ${draft.trim() && !busy ? 'color-mix(in srgb, var(--accent) 50%, transparent)' : 'color-mix(in srgb, var(--borderSecondary) 50%, transparent)'}`,
                            color: draft.trim() && !busy ? 'var(--accent)' : 'var(--textMuted)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0,
                            boxSizing: 'border-box',
                            transition: 'all .2s ease',
                            opacity: draft.trim() && !busy ? 1 : 0.4,
                        }}
                    >
                        <Icon name="send" size={17} color={draft.trim() && !busy ? 'var(--accent)' : 'var(--textMuted)'} />
                    </button>

                    {/* LIVE Toggle Button on the side */}
                    <button
                        type="button"
                        onClick={() => (liveOn || live.state === 'connecting' ? live.stop() : live.start())}
                        disabled={!mic.ok || listening}
                        title={liveOn ? 'Gemini Live active — click to stop' : 'Start Gemini Live continuous voice session'}
                        style={{
                            height: 38,
                            padding: '0 10px',
                            margin: 0,
                            marginBottom: 0,
                            borderRadius: 8,
                            cursor: mic.ok && !listening ? 'pointer' : 'not-allowed',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 4,
                            flexShrink: 0,
                            boxSizing: 'border-box',
                            transition: 'all .2s ease',
                            fontWeight: 700,
                            fontSize: 11,
                            letterSpacing: '.04em',
                            color: liveOn ? '#04140d' : 'var(--accent)',
                            background: liveOn ? 'var(--success, var(--success))' : 'color-mix(in srgb, var(--accent) 12%, transparent)',
                            border: `1px solid ${liveOn ? 'var(--success, var(--success))' : 'color-mix(in srgb, var(--accent) 45%, transparent)'}`,
                            opacity: !mic.ok || listening ? 0.45 : 1,
                            width: 'auto',
                        }}
                    >
                        <Icon name={liveOn ? 'stop_circle' : 'bolt'} size={16} fill={liveOn} color={liveOn ? '#04140d' : 'var(--accent)'} />
                        <span>{liveOn ? 'STOP' : 'LIVE'}</span>
                    </button>
                </div>
            </div>
        </div>
    );
}

function Bubble({ turn, pending = false }) {
    if (turn.role === 'system') {
        return (
            <div style={{
                display: 'flex', gap: 6, alignItems: 'flex-start', margin: '6px 0',
                fontSize: 11, color: turn.ok ? 'var(--success)' : 'var(--danger)',
            }}>
                <Icon name={turn.ok ? 'check_circle' : 'error'} size={13} color={turn.ok ? 'var(--success)' : 'var(--danger)'} />
                <span style={{ flex: 1, lineHeight: 1.45 }}>{turn.text}</span>
            </div>
        );
    }

    const isUser = turn.role === 'user';
    return (
        <div style={{ display: 'flex', justifyContent: isUser ? 'flex-end' : 'flex-start', margin: '6px 0' }}>
            <div style={{
                maxWidth: '86%', padding: '7px 11px', borderRadius: 10, fontSize: 12.5, lineHeight: 1.5,
                opacity: pending ? 0.6 : 1,
                background: isUser ? 'color-mix(in srgb, var(--accent) 12%, transparent)' : 'color-mix(in srgb, var(--bgTertiary) 35%, transparent)',
                border: `1px solid ${isUser ? 'color-mix(in srgb, var(--accent) 30%, transparent)' : 'color-mix(in srgb, var(--borderSecondary) 50%, transparent)'}`,
                borderBottomRightRadius: isUser ? 3 : 10,
                borderBottomLeftRadius: isUser ? 10 : 3,
            }}>
                {turn.text}
                {turn.detail && (
                    <div style={{ marginTop: 6, fontSize: 11, color: 'var(--textMuted)', whiteSpace: 'pre-wrap' }}>{turn.detail}</div>
                )}
            </div>
        </div>
    );
}

export default VoiceAgent;
